import { SealPill } from "@caint/design-system";
import type { Eixo } from "@caint/engine";
import { engine } from "../../lib/engine.ts";
import type { Panorama } from "../../lib/panorama.ts";
import { BarrasClassificadas, Radar, type SerieRadar, faixaImgrCor } from "../graficos.tsx";
import "../../styles/relatorio.css";
import "../../styles/apresentacao.css";

/** Paleta para diferenciar as séries de unidades no radar (cicla se faltar cor). */
const CORES = [
  "var(--primary)",
  "var(--coral-500, #E8664E)",
  "var(--mat-3)",
  "var(--mat-2)",
  "#7B4FB0",
  "#2D74B8",
];
const EIXOS: readonly Eixo[] = ["IMGO", "IMQA", "IMGR"];
const NOME_EIXO: Record<Eixo, string> = {
  IMGO: "Operacional",
  IMQA: "Assistencial",
  IMGR: "Riscos",
};
const MAX_RADAR = 6;

/**
 * Panorama da Rede: comparação entre unidades (visão de gestão/AUDIN). Consome o
 * agregado puro `panorama` (lib/panorama.ts): ranking por IMGQ, comparativo por
 * eixo, o que é SISTÊMICO × ISOLADO na rede, e a nota de comparabilidade por NA%.
 */
export function PanoramaRede({ panorama }: { panorama: Panorama }) {
  const { unidades, rede, nUnidades, nCiclos } = panorama;

  if (nUnidades === 0) {
    return (
      <div className="apr-print">
        <section className="m-card pad rel-sec">
          <div className="rel-sec-head">
            <h3>Panorama da Rede</h3>
          </div>
          <p className="spec">Ainda não há avaliações sincronizadas para comparar.</p>
        </section>
      </div>
    );
  }

  const rankingBarras = [
    {
      rotulo: "IMGQ por unidade",
      valores: unidades.map((u) => ({ nome: u.unidadeNome, valor: u.imgq * 100 })),
    },
  ];
  const radarUnidades = unidades.slice(0, MAX_RADAR);
  const radarSeries: SerieRadar[] = radarUnidades.map((u, i) => ({
    nome: u.unidadeNome,
    cor: CORES[i % CORES.length] ?? "var(--primary)",
    valores: EIXOS.map((e) => (u.subs[e] == null ? null : (u.subs[e] as number) * 100)),
  }));

  return (
    <div className="panorama-print apr-print">
      <div className="rel-cols">
        {/* KPIs da rede */}
        <section className="m-card pad rel-sec rel-span">
          <div className="rel-sec-head">
            <h3>Panorama da Rede</h3>
            <span className="spec">
              {nUnidades} unidade(s) · {nCiclos} ciclo(s)
            </span>
          </div>
          <div className="rel-stats">
            <div className="rel-stat">
              <span className="l">IMGQ médio</span>
              <span className="v">{engine.pct(rede.imgqMedio)}</span>
              <span className="f">
                min {engine.pct(rede.imgqMin)} · máx {engine.pct(rede.imgqMax)}
              </span>
            </div>
            <div className="rel-stat">
              <span className="l">Dispersão</span>
              <span className="v">±{engine.pct(rede.imgqDesvio)}</span>
              <span className="f">desvio entre unidades</span>
            </div>
            <div className="rel-stat">
              <span className="l">Travadas</span>
              <span className="v" style={{ color: rede.nTravadas > 0 ? "var(--danger)" : "var(--success)" }}>
                {rede.nTravadas}/{nUnidades}
              </span>
              <span className="f">selo em ≤ 59,9%</span>
            </div>
            <div className="rel-stat">
              <span className="l">Comparabilidade NA</span>
              <span className="v" style={{ color: rede.naAlerta ? "var(--danger)" : undefined }}>
                {rede.naSpread.toFixed(0)}pp
              </span>
              <span className="f">{rede.naAlerta ? "acima de 15pp" : "amplitude ok"}</span>
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
            {rede.distSelos.map((d) => (
              <span key={d.chave} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <SealPill chave={d.chave} />
                <b style={{ fontSize: 12 }}>×{d.n}</b>
              </span>
            ))}
          </div>
        </section>

        {/* Ranking por IMGQ */}
        <section className="m-card pad rel-sec">
          <div className="rel-sec-head">
            <h3>Ranking por IMGQ</h3>
            <span className="spec">checklist (pré-monitoramento)</span>
          </div>
          <BarrasClassificadas grupos={rankingBarras} />
          <div className="rel-table-wrap">
            <table className="rel-table">
              <thead>
                <tr>
                  <th>Unidade</th>
                  <th className="num">IMGQ</th>
                  <th>Selo</th>
                  <th className="num">CORE 0/1</th>
                  <th className="num">NA%</th>
                </tr>
              </thead>
              <tbody>
                {unidades.map((u) => (
                  <tr key={u.avaliacaoId}>
                    <td>
                      {u.unidadeNome}
                      {u.travado ? <span style={{ color: "var(--danger)" }}> · travado</span> : ""}
                    </td>
                    <td className="num" style={{ fontWeight: 700 }}>
                      {engine.pct(u.imgq)}
                    </td>
                    <td>
                      <SealPill chave={u.selo} />
                    </td>
                    <td className="num" style={u.coreCriticos > 0 ? { color: "var(--danger)" } : undefined}>
                      {u.coreCriticos}
                    </td>
                    <td className="num">{(u.naPct * 100).toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Comparativo por eixo */}
        <section className="m-card pad rel-sec">
          <div className="rel-sec-head">
            <h3>Comparativo por eixo</h3>
            <span className="spec">IMGO · IMQA · IMGR</span>
          </div>
          <div style={{ display: "grid", placeItems: "center" }}>
            <Radar eixos={EIXOS.map((e) => NOME_EIXO[e])} series={radarSeries} tamanho={260} />
          </div>
          {unidades.length > MAX_RADAR ? (
            <p className="spec rel-nota">
              Mostrando as {MAX_RADAR} de maior IMGQ (de {unidades.length}); as demais estão na tabela do
              ranking.
            </p>
          ) : null}
        </section>

        {/* Comuns × diferenças */}
        {nUnidades > 1 ? (
          <section className="m-card pad rel-sec">
            <div className="rel-sec-head">
              <h3>Comum × isolado na rede</h3>
              <span className="spec">onde agir em bloco</span>
            </div>

            <h4 className="rel-h4">Setores frágeis sistêmicos (≥ 2 unidades)</h4>
            {rede.setoresSistemicos.length === 0 ? (
              <p className="spec">Nenhum setor frágil se repete entre unidades — fragilidades são locais.</p>
            ) : (
              <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 12.5, lineHeight: 1.5 }}>
                {rede.setoresSistemicos.map((s) => (
                  <li key={s.code}>
                    <b>
                      {s.code} · {s.nome}
                    </b>{" "}
                    — frágil em {s.unidades.length} unidades ({s.unidades.join(", ")}). Ação de rede.
                  </li>
                ))}
              </ul>
            )}

            <h4 className="rel-h4">Itens CORE críticos recorrentes (≥ 2 unidades)</h4>
            {rede.itensCoreRecorrentes.length === 0 ? (
              <p className="spec">Nenhum item CORE em 0/1 se repete entre unidades.</p>
            ) : (
              <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 12.5, lineHeight: 1.5 }}>
                {rede.itensCoreRecorrentes.slice(0, 8).map((it) => (
                  <li key={it.itemId}>
                    <b>{it.code}</b> — {it.pergunta}{" "}
                    <span className="spec">({it.unidades.length} unidades)</span>
                  </li>
                ))}
              </ul>
            )}

            {rede.setoresIsolados.length > 0 ? (
              <p className="spec rel-nota">
                Fragilidades <b>isoladas</b> (1 unidade): {rede.setoresIsolados.map((s) => s.code).join(", ")}{" "}
                — trate no plano da própria unidade.
              </p>
            ) : null}
          </section>
        ) : (
          <section className="m-card pad rel-sec">
            <div className="rel-sec-head">
              <h3>Comparação de rede</h3>
            </div>
            <p className="spec">
              Há apenas uma unidade avaliada. O comparativo sistêmico × isolado aparece quando houver duas ou
              mais unidades com avaliação.
            </p>
          </section>
        )}

        {/* Nota de comparabilidade (NA%) */}
        <section className="m-card pad rel-sec">
          <div className="rel-sec-head">
            <h3>Nota de comparabilidade</h3>
            <span className="spec">% de itens NA por unidade</span>
          </div>
          <div className="rel-bars">
            {unidades.map((u) => {
              const napct = u.naPct * 100;
              const f = faixaImgrCor(100 - napct);
              return (
                <div key={u.avaliacaoId} className="rel-bar-row">
                  <span className="rel-bar-label" style={{ minWidth: 110 }} title={u.unidadeNome}>
                    {u.unidadeNome}
                  </span>
                  <div className="rel-bar-track">
                    <div className="rel-bar-fill" style={{ width: `${napct}%`, background: f.cor }} />
                  </div>
                  <span className="rel-bar-val">{napct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
          <p className="spec rel-nota">
            {rede.naAlerta ? (
              <>
                ⚠️ A diferença de NA% entre unidades ({rede.naSpread.toFixed(0)}pp) passa de 15pp. Mesmo IMGQ
                pode significar conjuntos de itens diferentes — compare com cautela (parecer técnico IMGQ).
              </>
            ) : (
              <>
                Amplitude de NA% dentro de 15pp — as unidades avaliaram universos comparáveis, o que sustenta
                a comparação direta dos IMGQ.
              </>
            )}
          </p>
        </section>
      </div>
    </div>
  );
}
